import { fetchAuth } from '../../../shared/api/ApiService'
import {
    IAdminDomain,
    IAdminDomainPatch,
    ILegalInfo,
    ILegalInfoPatch,
    TAdminDomainStatus,
} from '../model/types'

type TRecord = Record<string, unknown>

const value = (source: TRecord, camelCase: string, snakeCase: string): string => {
    const raw = source[camelCase] ?? source[snakeCase]
    return raw === undefined || raw === null ? '' : String(raw)
}

const normalizeStatus = (raw: unknown): TAdminDomainStatus => {
    const status = String(raw ?? '')

    const byDomainValue: Record<string, TAdminDomainStatus> = {
        pending_verification: 'ADMIN_DOMAIN_STATUS_PENDING_VERIFICATION',
        provisioning: 'ADMIN_DOMAIN_STATUS_PROVISIONING',
        active: 'ADMIN_DOMAIN_STATUS_ACTIVE',
        failed: 'ADMIN_DOMAIN_STATUS_FAILED',
        disabled: 'ADMIN_DOMAIN_STATUS_DISABLED',
        expired: 'ADMIN_DOMAIN_STATUS_EXPIRED',
        '1': 'ADMIN_DOMAIN_STATUS_PENDING_VERIFICATION',
        '2': 'ADMIN_DOMAIN_STATUS_PROVISIONING',
        '3': 'ADMIN_DOMAIN_STATUS_ACTIVE',
        '4': 'ADMIN_DOMAIN_STATUS_FAILED',
        '5': 'ADMIN_DOMAIN_STATUS_DISABLED',
        '6': 'ADMIN_DOMAIN_STATUS_EXPIRED',
    }

    if (status in byDomainValue) return byDomainValue[status]

    if (status.startsWith('ADMIN_DOMAIN_STATUS_') && status !== 'ADMIN_DOMAIN_STATUS_UNSPECIFIED') {
        return status as TAdminDomainStatus
    }

    return 'ADMIN_DOMAIN_STATUS_PENDING_VERIFICATION'
}

const mapLegalInfo = (source: TRecord): ILegalInfo => ({
    id: value(source, 'id', 'id'),
    adminId: value(source, 'adminId', 'admin_id'),
    fullName: value(source, 'fullName', 'full_name'),
    inn: value(source, 'inn', 'inn'),
    ogrnip: value(source, 'ogrnip', 'ogrnip'),
    registrationAddress: value(source, 'registrationAddress', 'registration_address'),
    email: value(source, 'email', 'email'),
    createdAt: value(source, 'createdAt', 'created_at'),
    updatedAt: value(source, 'updatedAt', 'updated_at'),
    updatedBy: value(source, 'updatedBy', 'updated_by'),
})

const mapAdminDomain = (source: TRecord): IAdminDomain => ({
    id: value(source, 'id', 'id'),
    adminId: value(source, 'adminId', 'admin_id'),
    hostname: value(source, 'hostname', 'hostname'),
    status: normalizeStatus(source.status),
    ownershipVerifiedAt: value(source, 'ownershipVerifiedAt', 'ownership_verified_at'),
    domainPaidUntil: value(source, 'domainPaidUntil', 'domain_paid_until'),
    certificateIssuedAt: value(source, 'certificateIssuedAt', 'certificate_issued_at'),
    certificateExpiresAt: value(source, 'certificateExpiresAt', 'certificate_expires_at'),
    certificateProvider: value(source, 'certificateProvider', 'certificate_provider'),
    nginxConfigAppliedAt: value(source, 'nginxConfigAppliedAt', 'nginx_config_applied_at'),
    lastError: value(source, 'lastError', 'last_error'),
    createdAt: value(source, 'createdAt', 'created_at'),
    updatedAt: value(source, 'updatedAt', 'updated_at'),
})

class LegalInfoService {
    async getLegalInfo(): Promise<ILegalInfo> {
        const response = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/legal-info`)
        const payload = await response.json() as TRecord
        return mapLegalInfo((payload.legalInfo ?? payload.legal_info ?? {}) as TRecord)
    }

    async patchLegalInfo(data: ILegalInfoPatch): Promise<ILegalInfo> {
        const response = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/legal-info`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        })
        const payload = await response.json() as TRecord
        return mapLegalInfo((payload.legalInfo ?? payload.legal_info ?? {}) as TRecord)
    }

    async getAdminDomain(): Promise<IAdminDomain> {
        const response = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/legal-info/domain`)
        const payload = await response.json() as TRecord
        return mapAdminDomain((payload.domain ?? {}) as TRecord)
    }

    async patchAdminDomain(data: IAdminDomainPatch): Promise<IAdminDomain> {
        const response = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/legal-info/domain`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        })
        const payload = await response.json() as TRecord
        return mapAdminDomain((payload.domain ?? {}) as TRecord)
    }

    async requestAdminDomainSync(): Promise<IAdminDomain> {
        const response = await fetchAuth(`${process.env.REACT_APP_SERVER_URL_ADMIN}/api/v1/legal-info/domain/sync`, {
            method: 'POST',
        })
        const payload = await response.json() as TRecord
        return mapAdminDomain((payload.domain ?? {}) as TRecord)
    }
}

export const legalInfoService = new LegalInfoService()
